/** @jsx jsx */
import { jsx } from '@emotion/react';
import styled from '@emotion/styled/macro';
import React from 'react';
import { Column, Table as VirtualTable, TableHeaderProps } from 'react-virtualized';
import 'react-virtualized/styles.css';
import { FaSort, FaSortDown, FaSortUp } from 'react-icons/fa';
import { primaryColor } from 'styles/colors';
import { adjustHexOpacity } from 'components/colorUtils';
import { TableProps } from './TableProps';

/**
 * A table widget
 * See documentation in TableProps for configuration details
 */
const Table: React.FC<TableProps> = ({
	data = [],
	columnNames: columns = [],
	readOnly = false,
	width,
	onSort,
	loadAdditionalRecords,
	sortBy,
	sortIsDescending,
	baseColor = primaryColor,
	minColumnWidth = 90,
	columnOverrides = {},
	...tableProps
}) => {
	const columnMargin: number = 10; //defined in react-virtualized ReactVirtualized__Table__headerColumn

	const minTableWidth = React.useMemo(() => {
		return (
			columnMargin + //the first column has margin on both sides.
			16 + //give some extra room for the y scrollbar
			(minColumnWidth + columnMargin) * columns?.length
		); //the sum of the widths of all the columns
	}, [columns, minColumnWidth]);

	//The onScroll event on the table fires on the initial render, which causes loadAdditionalRecords to fire unncessarily.
	// This const tracks whether this is the initial render so we don't call loadAdditionalRecords on the first render.
	const [isInitialRender, setIsInitialRender] = React.useState<boolean>(true);
	React.useEffect(() => setIsInitialRender(false), []);

	/* 
		react-virtualized's Table component doesn't support horizontal scrolling out-of-the-box
		source: https://nicedoc.io/bvaughn/react-virtualized/blob/master/docs/Table.md

		Rather than re-implement this whole module with a different library to support
		horizontal scrolling, we'll just do a little DOM manipulation.
	*/ 
	const [didAddScrollListener, setDidAddScrollListener] = React.useState<boolean>(false);
	const tableRef = React.useRef(null);
	React.useEffect(() => {
		if (tableRef && tableRef.current) {
			var headerEls = tableRef.current.getElementsByClassName('ReactVirtualized__Table__headerRow');
			var containerEls = tableRef.current.getElementsByClassName('ReactVirtualized__Grid__innerScrollContainer');
			if (headerEls.length == 0 || containerEls.length == 0) return;

			// set inner container (overflows beyond the container with the scrollbar) width to header width
			containerEls[0].style.width = headerEls[0].clientWidth + 'px';

			if (!didAddScrollListener) {
				var gridEls = tableRef.current.getElementsByClassName('ReactVirtualized__Table__Grid');
				if (gridEls.length == 0) return;

				gridEls[0].addEventListener('scroll', (e) => {
					headerEls[0].style.marginLeft = -e.target.scrollLeft + 'px';
				});
	
				setDidAddScrollListener(true);
			}
		}
	});

	const headerRenderer = React.useCallback(
		({ dataKey, label }: TableHeaderProps): React.ReactNode => {
			const isSorted: boolean = sortBy === dataKey;
			return (
				<div
					key={dataKey}
					title={typeof label === 'string' ? label : null}
					className="header-content"
					onClick={() => {
						onSort && onSort(dataKey, isSorted && !sortIsDescending);
					}}
				>
					<span className="ReactVirtualized__Table__headerTruncatedText">{label}</span>
					{!readOnly && (
						<span className={'sort-icon' + (isSorted ? '' : ' unsorted')}>
							{isSorted ? sortIsDescending ? <FaSortUp /> : <FaSortDown /> : <FaSort />}
						</span>
					)}
				</div>
			);
		},
		[sortBy, sortIsDescending, onSort, readOnly]
	);

	// Render your table
	return (
		<TableContainer ref={tableRef} baseColor={baseColor}>
			<VirtualTable
				width={width > minTableWidth ? width : minTableWidth}
				rowGetter={({ index }) => data[index]}
				rowStyle={({ index }) => {
					return index % 2 === 1 ? { backgroundColor: adjustHexOpacity(baseColor, 0.2) } : {};
				}}
				onScroll={({ clientHeight, scrollHeight, scrollTop }) => {
					//this number will be 0 when scrolling is all the way down
					const distanceToBottom = scrollHeight - clientHeight - scrollTop;
					if (!isInitialRender && distanceToBottom < 100) {
						loadAdditionalRecords && loadAdditionalRecords(); //fetch additional records
					}
				}}
				{...tableProps}
			>
				{columns.map((columnName, i) => {
					const columnOverride = columnOverrides[columnName] || {};

					return (
						<Column
							key={i}
							dataKey={columnName}
							label={columnName}
							width={minColumnWidth}
							minWidth={minColumnWidth}
							flexGrow={1}
							headerRenderer={headerRenderer}
							{...columnOverride}
						/>
					);
				})}
			</VirtualTable>
		</TableContainer>
	);
};

export { Table };

const TableContainer = styled.div<{ baseColor: string }>`
	overflow: hidden;
	height: 100%;
	font-size: 12px;

	.ReactVirtualized__Table__headerRow {
		background-color: ${({baseColor}) => adjustHexOpacity(baseColor, 0.9)};
		color: white;

		.header-content {
			display: flex;
			align-items: center;
			cursor: pointer;

			.ReactVirtualized__Table__headerTruncatedText {
				max-width: calc(100% - 0.75em);
				margin-right: 4px;
			}
			.sort-icon {
				font-size: 0.75em;
				padding-top: 0.25em;
				&.unsorted {
					display: none;
				}
			}
			:hover {
				.sort-icon {
					display: initial;
				}
			}
		}
	}

	.ReactVirtualized__Table__row {
		box-sizing: border-box;
		border: 1px solid transparent;

		&:hover {
			border: 1px solid ${(p) => p.baseColor};
		}
	}

	.ReactVirtualized__Table__Grid {
		width: 100% !important;
		overflow: auto !important;
	}
`;
